import React, { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LogOut } from "lucide-react";

/**
 * Logout confirmation dialog — required by Google Play testers.
 *
 * Wraps any child trigger (a button, a menu item, etc.) so calling logout is
 * always gated behind a "Confirm sign out?" AlertDialog. Uses the shared
 * shadcn AlertDialog so the visual style stays consistent with the rest of
 * the app's confirmation prompts (delete account, block user, etc.).
 *
 * Usage: wrap the logout trigger element in <LogoutConfirmDialog onConfirm={logout}>...</LogoutConfirmDialog>
 * — the wrapped child element gets an onClick handler that opens the dialog
 * instead of firing logout directly.
 */
export default function LogoutConfirmDialog({ onConfirm, children }) {
  const [open, setOpen] = useState(false);
  const trigger = React.cloneElement(children, {
    onClick: (e) => {
      e?.preventDefault?.();
      setOpen(true);
    },
  });
  return (
    <>
      {trigger}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#16171A] border-white/[0.08] text-[#F0E6D2]" data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <div className="w-12 h-12 mx-auto rounded-full bg-[#B95F73]/12 border border-[#B95F73]/30 flex items-center justify-center mb-2">
              <LogOut className="w-5 h-5 text-[#B95F73]" strokeWidth={2.2} />
            </div>
            <AlertDialogTitle className="font-display text-2xl tracking-tight text-center">
              Sign out of Spark?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-[#8C867C] text-sm leading-relaxed">
              You will be logged out of your current session. You can sign
              back in anytime with your email and password.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel
              className="bg-[#1B1B1E] border-white/[0.08] text-[#F0E6D2] hover:bg-[#232327]"
              data-testid="logout-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setOpen(false); onConfirm?.(); }}
              className="bg-[#B95F73] text-white hover:bg-[#A65266]"
              data-testid="logout-confirm-btn"
            >
              Confirm sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
